import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import type { AssessmentPatientContext } from '../types';
import { BIANCHI_2008_PCF_PERCENTILES, CARDOSO_2012_PCF_MEANS } from './peakCoughFlowReferenceData';

export const PEAK_COUGH_FLOW_SCHEMA_VERSION = 2;
export const PEAK_COUGH_FLOW_INTERPRETATION_NOTE = 'Interpretar em conjunto com os sintomas e a evolução clínica.';

export const PEAK_COUGH_FLOW_CONDITIONS = [
  { key: 'unassisted', label: 'Tosse espontânea', description: 'Tosse máxima após inspiração completa, sem assistência.' },
  { key: 'manual_assistance', label: 'Assistência manual', description: 'Assistência expiratória manual durante a tosse.' },
  { key: 'volume_recruitment', label: 'Após recrutamento de volume', description: 'Tosse após recrutamento de volume ou empilhamento de ar.' },
  { key: 'combined', label: 'Assistência combinada', description: 'Recrutamento de volume associado à assistência expiratória manual.' },
] as const;

export type PeakCoughFlowCondition = (typeof PEAK_COUGH_FLOW_CONDITIONS)[number]['key'];
export type PeakCoughFlowPosition = 'seated' | 'supine' | 'semi_recumbent' | 'other';
export type PeakCoughFlowInterface = 'oronasal_mask' | 'mouthpiece' | 'tracheostomy' | 'other';
export type PeakCoughFlowUnit = 'L/min' | 'L/s';

export const PEAK_COUGH_FLOW_POSITIONS: Record<PeakCoughFlowPosition, string> = {
  seated: 'Sentado', supine: 'Decúbito dorsal', semi_recumbent: 'Semissentado', other: 'Outra posição',
};
export const PEAK_COUGH_FLOW_INTERFACES: Record<PeakCoughFlowInterface, string> = {
  oronasal_mask: 'Máscara oronasal', mouthpiece: 'Bocal', tracheostomy: 'Traqueostomia', other: 'Outra interface',
};

export interface PeakCoughFlowAttempt {
  value: number;
  accepted: boolean;
  exclusionReason?: string | null;
}
export interface PeakCoughFlowSeries {
  condition: PeakCoughFlowCondition;
  attempts: PeakCoughFlowAttempt[];
  techniqueNotes?: string | null;
}
export interface PeakCoughFlowClinicalContext {
  neuromuscularDisease?: boolean;
  respiratoryInfection?: boolean;
  secretionClearanceDifficulty?: boolean;
  bulbarImpairment?: boolean;
}
export interface PeakCoughFlowMeasurementQuality {
  interfaceLeak?: boolean;
  fatigueLimited?: boolean;
}
export interface PeakCoughFlowResultPayload {
  schemaVersion: number;
  unit: 'L/min';
  ageYears?: number | null;
  sex?: 'male' | 'female' | null;
  heightCm?: number | null;
  weightKg?: number | null;
  measuredAt?: string | null;
  position?: PeakCoughFlowPosition | null;
  inclinationDegrees?: number | null;
  interface?: PeakCoughFlowInterface | null;
  device?: string | null;
  series: PeakCoughFlowSeries[];
  clinicalContext?: PeakCoughFlowClinicalContext;
  measurementQuality?: PeakCoughFlowMeasurementQuality;
  notes?: string | null;
  interpretation?: string | null;
  referenceNote?: string | null;
  finishedAt?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  report?: Record<string, unknown>;
}

export const PEAK_COUGH_FLOW_CLINICAL_FLAGS = [
  { key: 'respiratoryInfection', label: 'Infecção respiratória atual' },
  { key: 'secretionClearanceDifficulty', label: 'Dificuldade para eliminar secreções' },
  { key: 'bulbarImpairment', label: 'Comprometimento bulbar' },
] as const;

export const PEAK_COUGH_FLOW_MEASUREMENT_QUALITY_FLAGS = [
  { key: 'interfaceLeak', label: 'Escape de ar na interface' },
  { key: 'fatigueLimited', label: 'Tentativas limitadas por fadiga ou intolerância' },
] as const;

/** Shared numerical geometry for HTML and PDF; positions are absolute L/min. */
export interface PeakCoughFlowReferenceScale {
  id: string;
  title: string;
  subtitle?: string;
  value: number;
  min: number;
  max: number;
  segments: { from: number; to: number; color: string; label: string }[];
  markers: { value: number; label: string }[];
  classification: string;
  color: string;
  footnote?: string;
}

export interface PeakCoughFlowReferenceAssessment {
  available: boolean;
  referenceId: 'BIANCHI_2008' | 'CARDOSO_2012' | null;
  title: string;
  sourceLabel: string | null;
  classification: string | null;
  comparisonValue: number | null;
  statisticLabel: string | null;
  interpretation: string;
  reason: string | null;
  color: string;
  completedAgeYears: number | null;
  p5?: number;
  p50?: number;
  p95?: number;
  mean?: number;
  sd?: number;
  sampleSizePerAgeAndSex?: number;
  caveats: string[];
  scale?: PeakCoughFlowReferenceScale;
}

export interface PeakCoughFlowNmdAssessment {
  available: boolean;
  classification: string | null;
  interpretation: string;
  reason: string | null;
  color: string;
  scale?: PeakCoughFlowReferenceScale;
}

const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value.trim() : null;
const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const finiteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(typeof value === 'string' ? value.trim().replace(',', '.') : value);
  return Number.isFinite(parsed) ? parsed : null;
};
const enumValue = <T extends string>(value: unknown, values: readonly T[]): T | null =>
  typeof value === 'string' && values.includes(value as T) ? value as T : null;

export const normalizePeakCoughFlowSex = (value: unknown): 'male' | 'female' | null => {
  const normalized = typeof value === 'string' ? value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
  if (['m', 'male', 'masculino', 'homem'].includes(normalized)) return 'male';
  if (['f', 'female', 'feminino', 'mulher'].includes(normalized)) return 'female';
  return null;
};

const positiveSnapshot = (value: unknown): number | null => {
  const number = finiteNumber(value);
  return number != null && number > 0 ? number : null;
};

/** Canonical V2 snapshots distinguish an explicitly cleared field from a
 * missing legacy field. Reusing this merge at both persistence layers keeps
 * the saved result, row snapshots and reference decision equal to the preview. */
export const resolvePeakCoughFlowPatientContext = (value: unknown, fallback: AssessmentPatientContext): AssessmentPatientContext & { sex: 'male' | 'female' | null } => {
  const source = record(value) ?? {};
  const context = record(source.patientContext) ?? {};
  const canonicalSnapshot = (finiteNumber(source.schemaVersion) ?? 1) >= 2;
  const pick = (key: 'ageYears' | 'sex' | 'heightCm' | 'weightKg', aliases: string[] = []): unknown => {
    if (canonicalSnapshot) {
      if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) return source[key];
      if (Object.prototype.hasOwnProperty.call(context, key) && context[key] !== undefined) return context[key];
    }
    for (const candidate of [source[key], ...aliases.map(alias => source[alias]), context[key], ...aliases.map(alias => context[alias])]) {
      if (candidate != null) return candidate;
    }
    return fallback[key];
  };
  const age = finiteNumber(pick('ageYears', ['patientAgeYears', 'age']));
  const rawHeight = positiveSnapshot(pick('heightCm', ['height', 'patientHeightCm']));
  return {
    birthDate: typeof context.birthDate === 'string' ? context.birthDate : fallback.birthDate,
    ageYears: age != null && age >= 0 && age <= 130 ? age : null,
    sex: normalizePeakCoughFlowSex(pick('sex', ['gender', 'patientSex'])),
    heightCm: rawHeight != null && rawHeight < 10 ? Number((rawHeight * 100).toFixed(3)) : rawHeight,
    weightKg: positiveSnapshot(pick('weightKg', ['weight', 'patientWeightKg'])),
    identifier: typeof context.identifier === 'string' ? context.identifier : fallback.identifier,
  };
};

export const normalizePeakCoughFlowValue = (value: unknown, unit: PeakCoughFlowUnit = 'L/min'): number | null => {
  const parsed = finiteNumber(value);
  if (parsed == null || parsed <= 0) return null;
  const converted = Number((parsed * (unit === 'L/s' ? 60 : 1)).toFixed(3));
  return Number.isFinite(converted) && converted > 0 ? converted : null;
};

export const formatPeakCoughFlowValue = (value: number | null | undefined, decimals = 1): string =>
  value != null && Number.isFinite(value)
    ? value.toLocaleString('pt-BR', { maximumFractionDigits: decimals })
    : '—';

export const getPeakCoughFlowConditionLabel = (condition: PeakCoughFlowCondition): string =>
  PEAK_COUGH_FLOW_CONDITIONS.find((item) => item.key === condition)?.label ?? condition;

export const normalizePeakCoughFlowPayload = (value: unknown): PeakCoughFlowResultPayload | null => {
  const source = record(value);
  if (!source || (source.unit != null && source.unit !== 'L/min' && source.unit !== 'L/s')) return null;
  const inputUnit: PeakCoughFlowUnit = source.unit === 'L/s' ? 'L/s' : 'L/min';
  const sourceContext = record(source.patientContext);
  const clinical = record(source.clinicalContext);
  const quality = record(source.measurementQuality);
  const { ageYears, sex, heightCm, weightKg } = resolvePeakCoughFlowPatientContext(source, {
    ageYears: null, sex: null, heightCm: null, weightKg: null, birthDate: null, identifier: null,
  });
  const inclination = finiteNumber(source.inclinationDegrees);
  const allSeries = Array.isArray(source.series) ? source.series : [];
  const series: PeakCoughFlowSeries[] = [];
  for (const definition of PEAK_COUGH_FLOW_CONDITIONS) {
    const matching = allSeries.map(record).filter((item) => item?.condition === definition.key);
    if (!matching.length) continue;
    const attempts = matching.flatMap((item) => Array.isArray(item?.attempts) ? item.attempts : []).flatMap((item) => {
      const attempt = record(item);
      const flow = normalizePeakCoughFlowValue(attempt ? attempt.value : item, inputUnit);
      return flow == null ? [] : [{ value: flow, accepted: attempt?.accepted !== false, exclusionReason: text(attempt?.exclusionReason) }];
    });
    series.push({ condition: definition.key, attempts, techniqueNotes: matching.map((item) => text(item?.techniqueNotes)).filter(Boolean).join('; ') || null });
  }
  return {
    schemaVersion: PEAK_COUGH_FLOW_SCHEMA_VERSION,
    unit: 'L/min',
    ageYears, sex, heightCm, weightKg,
    measuredAt: text(source.measuredAt),
    position: enumValue(source.position, Object.keys(PEAK_COUGH_FLOW_POSITIONS) as PeakCoughFlowPosition[]),
    inclinationDegrees: inclination != null && inclination >= 0 && inclination <= 90 ? inclination : null,
    interface: enumValue(source.interface, Object.keys(PEAK_COUGH_FLOW_INTERFACES) as PeakCoughFlowInterface[]),
    device: text(source.device),
    series,
    clinicalContext: {
      neuromuscularDisease: clinical?.neuromuscularDisease === true,
      ...Object.fromEntries(PEAK_COUGH_FLOW_CLINICAL_FLAGS.map((flag) => [flag.key, clinical?.[flag.key] === true])),
    },
    measurementQuality: Object.fromEntries(PEAK_COUGH_FLOW_MEASUREMENT_QUALITY_FLAGS.map((flag) => [flag.key,
      typeof quality?.[flag.key] === 'boolean' ? quality[flag.key] : clinical?.[flag.key] === true,
    ])),
    notes: text(source.notes),
    interpretation: text(source.interpretation),
    referenceNote: text(source.referenceNote),
    finishedAt: text(source.finishedAt) ?? undefined,
    patientContext: { ...sourceContext, ageYears, sex, heightCm, weightKg },
    ...(record(source.professionalContext) ? { professionalContext: record(source.professionalContext)! } : {}),
    ...(record(source.report) ? { report: record(source.report)! } : {}),
  };
};

export interface PeakCoughFlowSeriesSummary {
  condition: PeakCoughFlowCondition;
  label: string;
  bestFlow: number | null;
  validAttemptCount: number;
  excludedAttemptCount: number;
  repeatabilityPercent: number | null;
  repeatable: boolean | null;
  gainAbsolute: number | null;
  gainPercent: number | null;
  warnings: string[];
}

const referenceScaleMax = (...values: number[]): number => Math.max(100, Math.ceil(Math.max(...values) * 1.1 / 100) * 100);

/** Comparisons retain the statistic reported in each source: pediatric maximum
 * versus empirical percentiles; adult mean of exactly three accepted coughs
 * versus a small age/sex sample mean. Neither P50 nor a group mean is labelled
 * an individual prediction, and adult SD is never converted into a clinical LLN. */
export const assessPeakCoughFlowHealthyReference = (payload: PeakCoughFlowResultPayload | null): PeakCoughFlowReferenceAssessment => {
  const age = payload?.ageYears;
  const completedAgeYears = age != null ? Math.floor(age) : null;
  const pediatric = completedAgeYears != null && (completedAgeYears < 18 || (completedAgeYears === 18 && payload?.interface !== 'mouthpiece'));
  const referenceId = completedAgeYears == null ? null : pediatric ? 'BIANCHI_2008' : 'CARDOSO_2012';
  const sourceLabel = referenceId === 'BIANCHI_2008' ? 'Bianchi e Baiardi, 2008' : referenceId === 'CARDOSO_2012' ? 'Cardoso et al., 2012' : null;
  const base: PeakCoughFlowReferenceAssessment = {
    available: false, referenceId, title: 'Comparação com referência saudável', sourceLabel,
    classification: null, comparisonValue: null, statisticLabel: null, interpretation: '', reason: null,
    color: '#475569', completedAgeYears, caveats: [],
  };
  const unavailable = (reason: string): PeakCoughFlowReferenceAssessment => ({ ...base, reason, interpretation: reason });
  if (completedAgeYears == null) return unavailable('A idade na avaliação não foi informada; não é possível selecionar a referência por anos completos.');
  if (completedAgeYears < 4 || completedAgeYears > 40) return unavailable(`Não há referência saudável cadastrada para ${completedAgeYears} anos completos; Bianchi abrange 4 a 18 anos e Cardoso, 18 a 40 anos.`);
  if (!payload?.sex) return unavailable('O sexo utilizado pelas tabelas de referência não foi informado ou não foi reconhecido.');
  if (payload.position !== 'seated') return unavailable(payload.position ? 'A referência saudável foi obtida na posição sentada e não é aplicada à posição registrada.' : 'Registre a posição sentada para verificar a compatibilidade com a referência saudável.');
  const expectedInterface = pediatric ? 'oronasal_mask' : 'mouthpiece';
  if (payload.interface !== expectedInterface) return unavailable(pediatric
    ? 'A referência pediátrica de Bianchi requer tosse espontânea medida por máscara oronasal; a interface registrada não corresponde ao protocolo.'
    : 'A comparação de Cardoso requer medida por bocal com protocolo Mini-Wright compatível; a interface registrada não corresponde a essa adaptação.');
  const spontaneous = payload.series.find(item => item.condition === 'unassisted');
  const values = spontaneous?.attempts.filter(attempt => attempt.accepted).map(attempt => attempt.value) ?? [];
  if (!values.length) return unavailable('Não há manobra aceita de tosse espontânea; medidas assistidas não substituem a condição usada nas referências saudáveis.');
  if (pediatric) {
    const percentiles = BIANCHI_2008_PCF_PERCENTILES[payload.sex][completedAgeYears];
    if (!percentiles) return unavailable('Não há percentis pediátricos tabulados para a idade e o sexo registrados.');
    const [p5, p50, p95] = percentiles;
    const comparisonValue = Math.max(...values);
    const classification = comparisonValue < p5 ? 'Abaixo do 5º percentil' : comparisonValue > p95 ? 'Acima do 95º percentil' : 'Entre o 5º e o 95º percentil';
    const color = comparisonValue < p5 ? '#b45309' : comparisonValue > p95 ? '#0369a1' : '#15803d';
    const caveats = [
      'Percentis empíricos de crianças e adolescentes saudáveis; P50 é a mediana da amostra, não um valor predito individual nem um limite clínico de normalidade.',
      'O estudo utilizou máscara facial e medidor Assess Peak Flow; diferenças entre instrumentos devem ser consideradas.',
      ...(values.length < 3 ? ['A classificação foi calculada com menos de três manobras aceitas; resultado parcial em relação ao protocolo do estudo.'] : []),
    ];
    const referenceBand = comparisonValue < p5 ? `abaixo do 5º percentil (${p5} L/min)`
      : comparisonValue > p95 ? `acima do 95º percentil (${p95} L/min)`
        : `entre P5 e P95 (${p5} a ${p95} L/min)`;
    const interpretation = `${formatPeakCoughFlowValue(comparisonValue)} L/min, ${referenceBand} para ${payload.sex === 'male' ? 'meninos' : 'meninas'} de ${completedAgeYears} anos completos.`;
    const max = referenceScaleMax(p95, comparisonValue);
    const scale: PeakCoughFlowReferenceScale = {
      id: 'BIANCHI_2008', title: 'Referência pediátrica por idade e sexo', subtitle: 'Maior tosse espontânea · L/min',
      value: comparisonValue, min: 0, max, classification, color,
      segments: [
        { from: 0, to: p5, color: '#fde68a', label: 'Abaixo de P5' },
        { from: p5, to: p95, color: '#bbf7d0', label: 'P5 a P95' },
        { from: p95, to: max, color: '#bae6fd', label: 'Acima de P95' },
      ],
      markers: [{ value: p5, label: 'P5' }, { value: p50, label: 'P50' }, { value: p95, label: 'P95' }],
      footnote: `Bianchi e Baiardi, 2008: P5 ${p5}, P50 ${p50} e P95 ${p95} L/min. P50 representa a mediana, não um predito individual. Percentis empíricos sem interpolação.`,
    };
    return { ...base, available: true, classification, comparisonValue, statisticLabel: 'Maior tosse espontânea', interpretation, color, p5, p50, p95, caveats, scale };
  }
  if (values.length !== 3) return unavailable(`Cardoso compara a média de exatamente três manobras aceitas de tosse espontânea; foram registradas ${values.length}. O maior valor permanece disponível, mas não substitui essa média.`);
  const row = CARDOSO_2012_PCF_MEANS.find(item => item[0] === completedAgeYears);
  if (!row) return unavailable('Não há média adulta tabulada para a idade registrada; a tabela de Cardoso abrange 18 a 40 anos completos.');
  const mean = payload.sex === 'male' ? row[1] : row[3];
  const sd = payload.sex === 'male' ? row[2] : row[4];
  const comparisonValue = values.reduce((sum, value) => sum + value, 0) / 3;
  const equalMean = Math.abs(comparisonValue - mean) < 1e-9;
  const classification = equalMean ? 'Na média da referência' : comparisonValue < mean ? 'Abaixo da média da referência' : 'Acima da média da referência';
  const color = '#475569';
  const caveats = [
    'Comparação descritiva com média e desvio-padrão de uma amostra de dez pessoas por sexo e idade, com IMC de 20 a 25 kg/m²; estar abaixo da média não define anormalidade.',
    'O estudo utilizou medidor Mini-Wright. A interface por bocal é uma adaptação baseada na configuração habitual do aparelho; não foi nomeada explicitamente no artigo.',
    'Não foram derivados limite inferior de normalidade, percentis ou valor predito individual a partir da média e do desvio-padrão.',
  ];
  const meanRelation = equalMean ? 'igual à' : comparisonValue < mean ? 'abaixo da' : 'acima da';
  const interpretation = `Média das três tosses: ${formatPeakCoughFlowValue(comparisonValue)} L/min, ${meanRelation} média publicada de ${mean} L/min para ${payload.sex === 'male' ? 'homens' : 'mulheres'} de ${completedAgeYears} anos completos. A comparação com a média não define anormalidade.`;
  const max = referenceScaleMax(mean + sd, comparisonValue);
  const scale: PeakCoughFlowReferenceScale = {
    id: 'CARDOSO_2012', title: 'Comparação descritiva com média adulta', subtitle: 'Média de três tosses espontâneas · L/min',
    value: comparisonValue, min: 0, max, classification, color,
    segments: [{ from: 0, to: mean, color: '#e2e8f0', label: 'Abaixo da média' }, { from: mean, to: max, color: '#cbd5e1', label: 'Acima da média' }],
    markers: [{ value: mean, label: 'Média' }],
    footnote: `Cardoso et al., 2012: média ${mean} ± ${sd} L/min (média ± DP), n=10 por sexo e idade; IMC de 20 a 25 kg/m². DP é descritivo e não representa limite de normalidade.`,
  };
  return { ...base, available: true, classification, comparisonValue, statisticLabel: 'Média de três tosses espontâneas', interpretation, color, mean, sd, sampleSizePerAgeAndSex: 10, caveats, scale };
};

export const summarizePeakCoughFlowPayload = (payload: unknown) => {
  const normalized = normalizePeakCoughFlowPayload(payload);
  const series: PeakCoughFlowSeriesSummary[] = (normalized?.series ?? []).map((item) => {
    const valid = item.attempts.filter((attempt) => attempt.accepted).map((attempt) => attempt.value).sort((a, b) => b - a);
    const bestFlow = valid[0] ?? null;
    const repeatabilityPercent = valid.length >= 2 ? (valid[0] - valid[1]) / valid[0] * 100 : null;
    const warnings: string[] = [];
    if (valid.length > 0 && valid.length < 3) warnings.push('Menos de três manobras aceitas; resultado parcial.');
    if (repeatabilityPercent != null && repeatabilityPercent >= 5) warnings.push('As duas maiores manobras diferem em 5% ou mais.');
    return {
      condition: item.condition, label: getPeakCoughFlowConditionLabel(item.condition), bestFlow,
      validAttemptCount: valid.length, excludedAttemptCount: item.attempts.length - valid.length,
      repeatabilityPercent, repeatable: repeatabilityPercent == null ? null : repeatabilityPercent < 5,
      gainAbsolute: null, gainPercent: null, warnings,
    };
  });
  const spontaneous = series.find((item) => item.condition === 'unassisted');
  const bestUnassistedFlow = spontaneous?.bestFlow ?? null;
  for (const item of series) {
    if (item.condition !== 'unassisted' && item.bestFlow != null && bestUnassistedFlow != null) {
      item.gainAbsolute = item.bestFlow - bestUnassistedFlow;
      item.gainPercent = (item.bestFlow - bestUnassistedFlow) / bestUnassistedFlow * 100;
    }
  }
  const primary = spontaneous?.bestFlow != null ? spontaneous : series.find((item) => item.bestFlow != null);
  const bestFlow = primary?.bestFlow ?? null;
  const adultNeuromuscular = normalized?.ageYears != null && normalized.ageYears >= 18 && normalized.clinicalContext?.neuromuscularDisease === true;
  const classifiable = adultNeuromuscular && bestUnassistedFlow != null &&
    (normalized?.interface === 'mouthpiece' || normalized?.interface === 'oronasal_mask');
  let severity: 'unclassified' | 'positive' | 'warning' | 'urgent' = 'unclassified';
  const referenceAssessment = assessPeakCoughFlowHealthyReference(normalized);
  let classification = bestFlow == null ? 'Sem manobras aceitas' : 'Referência não aplicável';
  let scoreColor = '#475569';
  if (classifiable) {
    if (bestUnassistedFlow >= 270) { severity = 'positive'; classification = 'Sem alerta por baixo fluxo'; scoreColor = '#15803d'; }
    else if (bestUnassistedFlow >= 160) { severity = 'warning'; classification = 'Fluxo de tosse reduzido'; scoreColor = '#b45309'; }
    else { severity = 'urgent'; classification = 'Fluxo de tosse muito reduzido'; scoreColor = '#be123c'; }
  } else if (referenceAssessment.available) {
    classification = referenceAssessment.classification!;
    scoreColor = referenceAssessment.color;
    if (referenceAssessment.referenceId === 'BIANCHI_2008') severity = referenceAssessment.comparisonValue! < referenceAssessment.p5! ? 'warning' : 'positive';
  }
  const nmdReason = classifiable ? null : bestUnassistedFlow == null ? 'Os limiares neuromusculares exigem uma manobra aceita de tosse espontânea.'
    : normalized?.ageYears == null ? 'Os limiares neuromusculares adultos exigem idade registrada.'
      : normalized.ageYears < 18 ? 'Os limiares de 160 e 270 L/min não são aplicados automaticamente a menores de 18 anos.'
        : normalized.clinicalContext?.neuromuscularDisease !== true ? 'Os limiares de 160 e 270 L/min se destinam ao contexto adulto de doença neuromuscular, não informado neste registro.'
          : 'Os limiares neuromusculares exigem medida espontânea por bocal ou máscara oronasal e não são aplicados à interface registrada.';
  const nmdInterpretation = !classifiable ? nmdReason! : bestUnassistedFlow! >= 270
    ? `${formatPeakCoughFlowValue(bestUnassistedFlow)} L/min, igual ou acima de 270 L/min: sem alerta por baixo fluxo nos limiares para adultos com DNM. Esse resultado não equivale a normalidade.`
    : bestUnassistedFlow! >= 160
      ? `${formatPeakCoughFlowValue(bestUnassistedFlow)} L/min, na faixa de 160 a menos de 270 L/min: fluxo de tosse reduzido nos limiares para adultos com DNM.`
      : `${formatPeakCoughFlowValue(bestUnassistedFlow)} L/min, abaixo de 160 L/min: fluxo de tosse muito reduzido nos limiares para adultos com DNM.`;
  const nmdAssessment: PeakCoughFlowNmdAssessment = { available: classifiable, classification: classifiable ? classification : null, interpretation: nmdInterpretation, reason: nmdReason, color: classifiable ? scoreColor : '#475569' };
  if (classifiable) {
    const max = Math.max(400, referenceScaleMax(bestUnassistedFlow!));
    nmdAssessment.scale = {
      id: 'PCF_ADULT_NMD_ATTENTION', title: 'Faixas de atenção na doença neuromuscular adulta', subtitle: 'Maior tosse espontânea · L/min',
      value: bestUnassistedFlow!, min: 0, max, classification, color: scoreColor,
      segments: [{ from: 0, to: 160, color: '#fecdd3', label: 'Abaixo de 160' }, { from: 160, to: 270, color: '#fde68a', label: '160 a menos de 270' }, { from: 270, to: max, color: '#bbf7d0', label: '270 ou mais' }],
      markers: [{ value: 160, label: '160' }, { value: 270, label: '270' }],
      footnote: 'Faixas clínicas de atenção; não equivalem a normalidade nem a critérios universais de extubação/decanulação.',
    };
  }
  const clinicalContextLabels: string[] = PEAK_COUGH_FLOW_CLINICAL_FLAGS.filter((flag) => normalized?.clinicalContext?.[flag.key]).map((flag) => flag.label);
  if (normalized?.clinicalContext?.neuromuscularDisease) clinicalContextLabels.unshift('Doença neuromuscular');
  const measurementQualityLabels = PEAK_COUGH_FLOW_MEASUREMENT_QUALITY_FLAGS.filter((flag) => normalized?.measurementQuality?.[flag.key]).map((flag) => flag.label);
  const warningMessages = series.flatMap((item) => item.warnings.map((warning) => `${item.label}: ${warning}`));
  if (normalized?.measurementQuality?.interfaceLeak) warningMessages.push('Escape na interface pode reduzir a confiabilidade da medida.');
  if (normalized?.measurementQuality?.fatigueLimited) warningMessages.push('Manobras limitadas por fadiga ou intolerância; considerar essa limitação na interpretação.');
  const automaticInterpretation = bestFlow == null ? 'Nenhuma manobra aceita foi registrada.'
    : classifiable ? nmdInterpretation
      : referenceAssessment.interpretation;
  const directInterpretation = text(record(payload)?.interpretation);
  const legacyAutomatic = directInterpretation != null && [PEAK_COUGH_FLOW_INTERPRETATION_NOTE,
    'Resultado descritivo; os limiares adultos não se aplicam a esta medida.', 'Nenhuma manobra aceita foi registrada.',
  ].includes(directInterpretation);
  // A report.interpretation is always an explicit professional override, even
  // if its exact wording happens to match a former automatic interpretation.
  const interpretation = resolveAssessmentInterpretationText(legacyAutomatic ? { ...record(payload), interpretation: null } : payload, automaticInterpretation);
  const technicalDetails = [
    normalized?.position ? `Posição: ${PEAK_COUGH_FLOW_POSITIONS[normalized.position]}` : null,
    normalized?.inclinationDegrees != null ? `Inclinação: ${formatPeakCoughFlowValue(normalized.inclinationDegrees)}°` : null,
    normalized?.interface ? `Interface: ${PEAK_COUGH_FLOW_INTERFACES[normalized.interface]}` : null,
    normalized?.device ? `Equipamento: ${normalized.device}` : null,
  ].filter((item): item is string => Boolean(item));
  return {
    bestFlow, bestUnassistedFlow, primaryCondition: primary?.condition ?? null,
    primaryConditionLabel: primary?.label ?? '', classification, severity, scoreColor,
    interpretation, patientInterpretation: interpretation, series, technicalDetails,
    clinicalContextLabels, measurementQualityLabels, warningMessages, adultNeuromuscular, classifiable,
    referenceAssessment, nmdAssessment,
    referenceScales: [nmdAssessment.scale, referenceAssessment.scale].filter((scale): scale is PeakCoughFlowReferenceScale => scale != null),
    referenceNote: 'Bianchi e Baiardi 2008: percentis empíricos por idade/sexo de 4 a 18 anos completos, sem percentil interpolado ou predito individual. Cardoso et al. 2012: média de três tosses comparada à média e DP de dez pessoas por sexo/idade (18 a 40 anos completos), sem limite de normalidade derivado. ERS 2019; CHEST 2023; consenso DMD UK 2024: os limiares de 270 e 160 L/min apoiam a avaliação de adultos com doença neuromuscular; não constituem diagnóstico isolado nem critérios universais de extubação. Não aplicados automaticamente a menores de 18 anos, idade ausente, outra população ou medida pela traqueostomia.',
  };
};

export const getPeakCoughFlowScoreColor = (payload: unknown): string => summarizePeakCoughFlowPayload(payload).scoreColor;

export const comparePeakCoughFlowProtocols = (current: unknown, previous: unknown, condition: PeakCoughFlowCondition = 'unassisted'): { comparable: boolean; note?: string } => {
  const a = normalizePeakCoughFlowPayload(current);
  const b = normalizePeakCoughFlowPayload(previous);
  if (!a || !b) return { comparable: false, note: 'Dados insuficientes para comparar.' };
  if (!a.series.some((item) => item.condition === condition && item.attempts.some((attempt) => attempt.accepted)) ||
      !b.series.some((item) => item.condition === condition && item.attempts.some((attempt) => attempt.accepted))) {
    return { comparable: false, note: 'Condição de tosse sem medidas aceitas em uma das avaliações.' };
  }
  if (!a.position || !b.position || !a.interface || !b.interface || !a.device || !b.device) {
    return { comparable: false, note: 'Posição, interface e equipamento devem estar registrados nas duas avaliações.' };
  }
  if (a.position === 'other' || b.position === 'other' || a.interface === 'other' || b.interface === 'other' ||
      (a.position === 'semi_recumbent' && (a.inclinationDegrees == null || b.inclinationDegrees == null))) {
    return { comparable: false, note: 'Posição ou interface inespecífica, ou inclinação sem registro, impedem a comparação direta.' };
  }
  if (a.position !== b.position || a.inclinationDegrees !== b.inclinationDegrees || a.interface !== b.interface || a.device.trim().toLowerCase() !== b.device.trim().toLowerCase()) {
    return { comparable: false, note: 'Posição, inclinação, interface ou equipamento diferentes entre as avaliações.' };
  }
  return { comparable: true };
};
